import React, { useRef, useEffect, useCallback, useState } from 'react';

export interface ScrollbarProps {
  scrollElement: HTMLElement | null;
  className?: string;
  onScroll?: () => void;
  showOnScrollOnly?: boolean;
}

export const Scrollbar: React.FC<ScrollbarProps> = ({
  scrollElement,
  className = '',
  onScroll,
  showOnScrollOnly = false
}) => {
  const [scrollState, setScrollState] = useState({
    left: 0,
    thumbWidth: 50,
    isScrollable: false
  });
  const [isScrolling, setIsScrolling] = useState(false);
  const [showThumb, setShowThumb] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);

  const updateScrollbar = useCallback((isUserScroll = false) => {
    if (!scrollElement) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollElement;
    const maxScroll = scrollWidth - clientWidth;
    
    if (isUserScroll) {
      setIsScrolling(true);
      
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
      
      scrollTimeout.current = setTimeout(() => {
        setIsScrolling(false);
      }, 1000);
    }
    
    if (scrollWidth > clientWidth) {
      if (!trackRef.current) {
        setScrollState({
          left: 0,
          thumbWidth: 50,
          isScrollable: true
        });
        return;
      }
      
      const trackWidth = trackRef.current.clientWidth;
      const scrollPercentage = scrollLeft / maxScroll;
      const thumbWidth = Math.max(50, (clientWidth / scrollWidth) * trackWidth);
      const maxThumbPosition = trackWidth - thumbWidth;
      const thumbPosition = scrollPercentage * maxThumbPosition;

      setScrollState({
        left: thumbPosition,
        thumbWidth: thumbWidth,
        isScrollable: true
      });
    } else {
      setScrollState({
        left: 0,
        thumbWidth: 50,
        isScrollable: false
      });
    }

    if (onScroll) {
      onScroll();
    }
  }, [scrollElement, onScroll]);

  const handleThumbMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!scrollElement || !trackRef.current) return;

    isDragging.current = true;
    setIsScrolling(true);
    
    const startX = e.clientX;
    const startScrollLeft = scrollElement.scrollLeft;
    const { scrollWidth, clientWidth } = scrollElement;
    const trackWidth = trackRef.current.clientWidth;
    const maxScroll = scrollWidth - clientWidth;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      
      const availableTrackSpace = trackWidth - scrollState.thumbWidth;
      if (availableTrackSpace <= 0) return;
      
      const deltaX = e.clientX - startX;
      const scrollRatio = deltaX / availableTrackSpace;
      const newScrollLeft = startScrollLeft + (scrollRatio * maxScroll);
      
      scrollElement.scrollLeft = Math.max(0, Math.min(newScrollLeft, maxScroll));
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
      
      scrollTimeout.current = setTimeout(() => {
        setIsScrolling(false);
      }, 1000);
      
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [scrollElement, scrollState.thumbWidth]);

  const handleTrackClick = useCallback((e: React.MouseEvent) => {
    if (!scrollElement || !trackRef.current) return;
    
    const trackRect = trackRef.current.getBoundingClientRect();
    const clickX = e.clientX - trackRect.left;
    const trackWidth = trackRect.width;
    const { scrollWidth, clientWidth } = scrollElement;
    const maxScroll = scrollWidth - clientWidth;
    
    const targetPosition = (clickX / trackWidth) * maxScroll;
    scrollElement.scrollLeft = targetPosition;
  }, [scrollElement]);

  useEffect(() => {
    if (!scrollElement) return;

    updateScrollbar(false);

    const handleScroll = () => updateScrollbar(true);
    scrollElement.addEventListener('scroll', handleScroll);

    const resizeObserver = new ResizeObserver(() => updateScrollbar(false));
    resizeObserver.observe(scrollElement);

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, [scrollElement, updateScrollbar]);

  useEffect(() => {
    if (trackRef.current && scrollState.isScrollable) {
      updateScrollbar(false);
    }
  }, [scrollState.isScrollable, updateScrollbar]);
  
  useEffect(() => {
    if (scrollState.isScrollable && !showThumb) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setShowThumb(true);
        });
      });
    } else if (!scrollState.isScrollable && showThumb) {
      setShowThumb(false);
    }
  }, [scrollState.isScrollable, showThumb]);

  return (
    <div className={`scrollbar ${showOnScrollOnly && !isScrolling ? 'scrollbar--hidden' : ''} ${className}`}>
      <div 
        className={`scrollbar__track ${!showThumb ? 'scrollbar__track--hidden' : ''}`}
        ref={trackRef}
        onClick={scrollElement && scrollState.isScrollable ? handleTrackClick : undefined}
      >
        <div 
          className={`scrollbar__thumb ${isScrolling ? 'scrollbar__thumb--scrolling' : ''} ${!showThumb ? 'scrollbar__thumb--hidden' : ''}`}
          style={{
            width: `${scrollState.thumbWidth}px`,
            transform: `translateX(${scrollState.left}px)`
          }}
          onMouseDown={scrollElement && scrollState.isScrollable ? handleThumbMouseDown : undefined}
        />
      </div>
    </div>
  );
};

